import {NgModule} from '@angular/core';
import {RouterModule, Routes} from '@angular/router';

import {EnsureUserAuthGuard} from '@usercommon/guards/auth.guards';
import {NeqAccountPage} from './neq/pages/account/account.page';
import {NeqLoginPage} from './login/login.page';
import {NeqPage} from './neq/neq.page';
import {RedirectGuard} from '@sharedcommon/guards/redirect.guard';
import {Rights} from '@sharedcommon/util/rights.util';
import {RightsGuard} from '@sharedcommon/guards/rights.guard';
import {environment} from '@sharedenvironment';
import {TranslateCanActivate} from '@sharedcommon/guards/translate.guard';
import {NeqRawmaterialsListPage} from './neq/pages/rawmaterials/rawmaterials-list.page';
import {NeqRawmaterialsPropertiesPage} from './neq/pages/rawmaterials/rawmaterials-properties.page';
import {NeqRawmaterialsOrSamplesManagePage} from './neq/pages/rawmaterials-or-samples/rawmaterials-or-samples-manage.page';
import {RawMaterialGuard} from '@usercommon/guards/rawmaterial.guard';
import {HasEntityGuard} from '@usercommon/guards/has-entity.guards';
import {NeqEntityChoicePage} from '@usercommon/components/entity-choice/entity-choice.page';
import {NeqExportSettingsPage} from './neq/pages/export/settings/export-settings.page';
import {RawMaterialMatricesResolver} from '@usercommon/resolvers/rawmaterial-matrices-resolver';
import { MsalGuard, MsalRedirectComponent } from '@azure/msal-angular';
import { NeqLogoutPage } from './logout/logout.page';
import { StoredUserGuard } from '@usercommon/guards/store-user.guard';
import { NeqSamplesListPage } from './neq/pages/samples/samples-list/samples-list.page';
import { HomeRedirectionPage } from './neq/pages/home-redirection/home-redirection.page';
import { OperatingModeGuard } from '@usercommon/guards/operating-mode.guard';
import { OperatingModeUtil } from '@sharedcommon/util/operating-mode.util';
import { NeqRawmaterialsMultiEditPage } from './neq/pages/rawmaterials/rawmaterials-multi-edit.page';
import { NeqSamplesPropertiesPage } from './neq/pages/samples/sample-properties/samples-properties.page';
import { NeqErrorPage } from './error/error.page';

const DATA_FOR_DUPLICATE = {rights: [Rights.U_MP_CALCULATE]} ;

const mainRouter: Routes = [
  {
    path: 'admin-site',
    canActivate: [RedirectGuard],
    component: HomeRedirectionPage,
    data: {
      externalUrl: environment.ADMIN_URL
    }
  },
  {path: 'login', component: NeqLoginPage},
  {path: 'logout', component: NeqLogoutPage},
  {path: 'entity', component: NeqEntityChoicePage, canActivate: [EnsureUserAuthGuard, MsalGuard], data: {site: 'user'}},

  {
    path: 'app',
    component: NeqPage,
    canActivate: [EnsureUserAuthGuard, MsalGuard, StoredUserGuard],
    canActivateChild: [HasEntityGuard, TranslateCanActivate, RightsGuard],
    children: [
      {
        path: '', component: HomeRedirectionPage
      },
      {
        path: 'samples', canActivate: [OperatingModeGuard], data: { operatingMode: OperatingModeUtil.SAMPLE_MODE }, children : [
          { path:'', component: NeqSamplesListPage },
          { path:'create', component: NeqSamplesPropertiesPage, data: { rights: [Rights.U_SAMPLE_CREATE ]} },
          { path: 'edit/:id', component: NeqSamplesPropertiesPage  },
          { path: 'input/:id', component: NeqRawmaterialsOrSamplesManagePage }
        ]
      },
      {
        path: 'rawmaterials', canActivate: [OperatingModeGuard], data: { operatingMode: OperatingModeUtil.FEED_MODE }, children: [
          {path: '', component: NeqRawmaterialsListPage},
          {path: 'create', component: NeqRawmaterialsPropertiesPage, data: {rights: [Rights.U_MP_CALCULATE]}},
          {
            path: 'duplicate',
            component: NeqRawmaterialsPropertiesPage,
            data: DATA_FOR_DUPLICATE
          },
          {
            path: 'edit/:id',
            component: NeqRawmaterialsPropertiesPage,
            canActivate: [RawMaterialGuard],
            data: {rights: [Rights.U_MP_CALCULATE]}
          },
          {
            path: 'multiedit',
            component: NeqRawmaterialsMultiEditPage,
            data: {rights: [Rights.U_MP_MASS_EDIT]}
          },
          {
            path: 'input/:id',
            children: [
              {
                path:'',
                component: NeqRawmaterialsOrSamplesManagePage,
                resolve: { matrices: RawMaterialMatricesResolver }
              },
              /* Use children to make it easy to go back in the raw material propertie page */
              {
                path: 'duplicate',
                component: NeqRawmaterialsPropertiesPage,
                data: DATA_FOR_DUPLICATE
              }
            ],
            canActivateChild: [RawMaterialGuard]
          },
          {path: 'export/settings', component: NeqExportSettingsPage, data: {rights: [Rights.U_MP_EXPORT_SETTINGS]}}
        ]
      },
      {path: 'account', component: NeqAccountPage}
    ],
  },
  {path: 'auth', component: MsalRedirectComponent}, // Dedicated route for redirects
  {path: 'error', component: NeqErrorPage},
  {path: '', redirectTo: 'app', pathMatch: 'full'}
];

@NgModule({
  imports: [
    RouterModule.forRoot(mainRouter, { useHash: false, relativeLinkResolution: 'legacy' }), // , enableTracing: true
  ],
  exports: [
    RouterModule
  ]
})
export class MainRoutingModule {
}
